/* eslint-disable import/no-restricted-paths */
import { useApolloClient } from "@apollo/client";
import { useCallback, useState } from "react";

import { uploadPublicImg } from "@/lib/aws-amplify/host-storage";
import { getURL } from "@/lib/aws-amplify/storage";
import type { InputMaybe } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";
import { imageMastInteractor } from "@/components/features/snack/_shared/repository/image-mast";

import type { DesignSnackLayoutSettingsFormModelOnSetViewModelType } from "../..";
import type { DesignSnackBlocksFormModel } from "../../type";
import type { S3Object, Scalars } from "@CHILLNN-Inc/chillnn-abr-core";
import type { StudioImageMast } from "@CHILLNN-Inc/chillnn-abr-studio";

type Props = {
	viewModel: DesignSnackBlocksFormModel["ViewModel"];
	onSetViewModel: DesignSnackLayoutSettingsFormModelOnSetViewModelType;
};

export type SnackBlocksFormModelLogoImageMethodsType = {
	isShowSelectLogoImageModal: boolean;
	activateSelectLogoImageModal: () => void;
	deactivateSelectLogoImageModal: () => void;
	onOpenChange: (input: boolean) => void;

	logoImage: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetLogoImage: (input: string) => void;
	onDeleteLogoImage: () => void;

	imageRepository: () => Promise<StudioImageMast[]>;
	imageUploader: (image: File) => Promise<Scalars["AWSURL"]>;
};

export const useSnackSettingsFormModelLogoImageMethods = ({
	viewModel,
	onSetViewModel,
}: Props): SnackBlocksFormModelLogoImageMethodsType => {
	const { logoImage, hotelID, companyID } = viewModel;
	const apolloClient = useApolloClient();
	const [isShowSelectLogoImageModal, setIsShowSelectBgImageModal] = useState<boolean>(false);
	const activateSelectLogoImageModal = useCallback(() => {
		setIsShowSelectBgImageModal(true);
	}, [setIsShowSelectBgImageModal]);
	const deactivateSelectLogoImageModal = useCallback(() => {
		setIsShowSelectBgImageModal(false);
	}, [setIsShowSelectBgImageModal]);

	const onOpenChange = useCallback(
		(input: boolean) => {
			setIsShowSelectBgImageModal(input);
		},
		[setIsShowSelectBgImageModal],
	);

	const onDeleteLogoImage = useCallback(() => {
		onSetViewModel([
			{
				key: "logoImage",
				value: {
					isEdited: true,
					value: undefined,
				},
			},
		]);
	}, [onSetViewModel]);

	const onSetLogoImage = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "logoImage",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const imageRepository = useCallback<SnackBlocksFormModelLogoImageMethodsType["imageRepository"]>(async () => {
		const imageMasts = await imageMastInteractor(apolloClient).fetchMast(hotelID);

		return imageMasts.map((imageMast) => {
			return {
				...imageMast,
				src: getURL(imageMast.image),
			} satisfies StudioImageMast;
		});
	}, [apolloClient, hotelID]);

	const imageUploader = useCallback<SnackBlocksFormModelLogoImageMethodsType["imageUploader"]>(
		async function (image: File): Promise<Scalars["AWSURL"]> {
			const path = `snack/blockLogoImg/${companyID}/${hotelID}`;
			const fileName = `${new Date().getTime()}.blockLogoImg`;
			const imageMast = await uploadPublicImg(path, fileName, image);
			await imageMastInteractor(apolloClient).addNewImage(hotelID, imageMast);

			return getURL({
				bucket: imageMast.bucket,
				key: imageMast.key,
				region: imageMast.region,
				mimeType: imageMast.mimeType,
				fileName: imageMast.fileName,
			} satisfies S3Object);
		},
		[apolloClient, companyID, hotelID],
	);

	return {
		isShowSelectLogoImageModal,
		activateSelectLogoImageModal,
		deactivateSelectLogoImageModal,
		onOpenChange,

		logoImage,
		onSetLogoImage,
		onDeleteLogoImage,

		imageRepository,
		imageUploader,
	};
};
